import type { TimelineEntry } from './api.ts'

/**
 * How long each checkpoint took, from the verification's own timeline.
 *
 * A checkpoint's duration is the gap to the next entry -- the next checkpoint
 * or the status that closed it. The last one, with nothing after it, is still
 * running, and is measured against `now` so the number on screen keeps
 * counting.
 *
 * Pure, so it is testable without a screen and so the screen cannot disagree
 * with it. This is the answer to "why is it slow": read the biggest number.
 */
export interface StepDuration {
  readonly ms: number
  /** Still counting: no later entry has closed it. */
  readonly live: boolean
}

export function stepDurations(
  timeline: readonly TimelineEntry[] | undefined,
  now: Date = new Date(),
): ReadonlyMap<string, StepDuration> {
  const out = new Map<string, StepDuration>()
  if (timeline === undefined) return out
  const entries = timeline
    .map((one) => ({ ...one, when: new Date(one.at).getTime() }))
    .filter((one) => Number.isFinite(one.when))

  for (const [index, one] of entries.entries()) {
    if (one.step === undefined || one.step === null) continue
    const next = entries[index + 1]
    const end = next === undefined ? now.getTime() : next.when
    /*
     * A checkpoint met twice -- a reverify runs the sequence again -- keeps its
     * latest duration. The strip is about this run.
     */
    out.set(one.step, { ms: Math.max(0, end - one.when), live: next === undefined })
  }
  return out
}

/** "3s", "1m 48s", "2h 05m": what a person reads off a step. */
export function humanDuration(ms: number): string {
  const seconds = Math.round(ms / 1000)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ${String(seconds % 60).padStart(2, '0')}s`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ${String(minutes % 60).padStart(2, '0')}m`
}
